import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  CircularProgress,
} from '@mui/material';

import {
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

import { SupporterProfileForm } from './components';

import { supporterService, uploadService } from '../../services';
import { validator } from '../../utils';
import { useSnack } from '../../hooks';
import { CancelButton, PrimaryButton, SecondaryButton } from '../../components/styles/buttons/index';
import { Supporter } from '../../models';

function SupporterInfoPage() {
  const { id } = useParams();
  const { setSnack } = useSnack();
  const navigate = useNavigate();

  const [state, setState] = useState({
    loading: true,
    edit: false,
    saving: false,
    dialogOpen: false,
  });

  const [profile, setProfile] = useState(new Supporter({}));

  useEffect(() => {
    async function fetchProfile() {
      if (parseInt(id, 10)) {
        const loadedData = (await supporterService.get(id)) || {};
        setProfile(loadedData);
        setState((oldState) => ({
          ...oldState,
          loading: false,
          edit: !loadedData?.id,
          dialogOpen: false,
        }));
      } else {
        setState((oldState) => ({
          ...oldState,
          loading: false,
          edit: true,
          dialogOpen: false,
        }));
      }
    }

    fetchProfile();
  }, [id]);

  const [errors, setErrors] = useState({
    name: '',
  });

  function isNewSupporter() {
    return profile?.id == null;
  }

  const cancel = () => {
    navigate('/supporters');
  };

  const toggleEdit = () => {
    if (parseInt(profile?.id, 10)) {
      setState({
        ...state,
        edit: !state.edit,
      });
    } else {
      cancel();
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setState({
      ...state,
      saving: true,
    });

    const image = (profile.file?.size || 0) === 0
      ? profile.image
      : await uploadService.image(profile.file);

    profile.image = image;

    const nameError = validator.name(profile.name);

    if (!nameError) {
      try {
        if (isNewSupporter()) {
          await supporterService.create(profile);
        } else {
          await supporterService.update(profile);
        }

        setState({
          ...state,
          loading: false,
          saving: false,
          edit: isNewSupporter(),
        });

        if (isNewSupporter()) {
          setProfile(new Supporter({}));
        }

        return setSnack({
          type: 'success',
          message: 'Cadastro salvo com sucesso',
        });
      } catch (e) {
        setState({
          ...state,
          saving: false,
        });
        return setSnack({ message: e.message });
      }
    } else {
      return setErrors({ name: nameError });
    }
  };

  const clearErrors = () => {
    const { name } = errors;

    // Avoid setState cycle
    if (name) {
      setErrors({ name: '' });
    }
  };

  function handleDelete() {
    supporterService.delete(profile.id).then((success) => {
      if (success) {
        cancel();
        setSnack({
          type: 'success',
          message: 'Apoiador apagado com sucesso',
        });
      } else {
        setSnack();
      }
    }).catch((error) => {
      setSnack({ message: error.message });
    });
  }

  const onDialogClose = (confirm) => {
    setState({ ...state, dialogOpen: false });

    if (confirm) {
      handleDelete();
    }
  };

  const confirmDelete = () => {
    setState({ ...state, dialogOpen: true });
  };

  const buttonRow = () => {
    const buttons = [];

    if (state.edit) {
      if (!isNewSupporter()) {
        buttons.push(
          <CancelButton
            key="deleteButton"
            onClick={confirmDelete}
            sx={{ mr: 2, color: 'error.main' }}
          >
            <DeleteIcon sx={{ mr: 2 }} />
            {' '}
            Deletar
          </CancelButton>,
        );
      }
      buttons.push(
        <SecondaryButton
          key="cancelEditButton"
          onClick={toggleEdit}
          sx={{ mr: 2 }}
          variant="secondaryButton"
        >
          Cancelar
        </SecondaryButton>,
      );
      buttons.push(
        <PrimaryButton
          type="submit"
          key="submitbutton"
          variant="primaryButton"
        >
          Salvar
        </PrimaryButton>,
      );
    } else {
      buttons.push(
        <SecondaryButton
          key="backButton"
          onClick={cancel}
          sx={{ mx: 2 }}
          variant="secondaryButton"
        >
          Voltar
        </SecondaryButton>,
      );
      buttons.push(
        <PrimaryButton
          key="editbutton"
          variant="primaryButton"
          onClick={toggleEdit}
        >
          Editar
          <EditIcon sx={{ ml: 2 }} />
        </PrimaryButton>,
      );
    }

    return buttons;
  };

  return state.loading
    ? (
      <CircularProgress />
    )
    : (
      <Box sx={{ display: 'flex' }}>
        <SupporterProfileForm
          handleSubmit={handleSubmit}
          errors={errors}
          clearErrors={clearErrors}
          profile={profile}
          setProfile={setProfile}
          isEditMode={state.edit}
          buttons={buttonRow()}
          dialogOpen={state.dialogOpen}
          onDialogClose={onDialogClose}
          loading={state.saving}
        />
      </Box>
    );
}

export default SupporterInfoPage;
